<template>
   
    <div class="flex min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-300">
      <!-- Sidebar -->
      <HrSidebar :isOpen="isSidebarOpen" />
  
      <!-- Main Content -->
      <main 
        class="flex-1 overflow-x-hidden transition-all duration-300"
        :class="{'ml-64': isSidebarOpen, 'ml-0': !isSidebarOpen}"
      >
        <!-- Navbar -->
        <HrTopbar 
          :page-title="currentPageTitle"
          :is-sidebar-open="isSidebarOpen"
          :is-dark-mode="isDarkMode"
          @toggle-sidebar="toggleSidebar"
          @toggle-dark-mode="toggleDarkMode"
        />
    <div class="max-w-8xl mx-auto container px-4 sm:px-6 lg:px-8 py-6 mt-4 mb-4 mx-auto p-6 bg-white rounded-lg shadow-md">
      <!-- Summary Cards -->
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <!-- Total Appraisals -->
        <div class="bg-white p-6 rounded-xl shadow-md border-l-4 border-[#035a76]">
          <div class="flex justify-between items-center">
            <div>
              <p class="text-sm font-medium text-[#035a76]">Total Appraisals</p>
              <p class="text-2xl font-bold text-[#035a76] mt-1">{{ totalAppraisals }}</p>
            </div>
            <div class="p-3 rounded-full bg-blue-100 text-[#035a76]">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01m-.01 4h.01" />
              </svg>
            </div>
          </div>
          <p class="text-xs text-[#035a76] mt-2">All completed appraisals</p>
        </div>
  
        <!-- Pending Reviews -->
        <div class="bg-white p-6 rounded-xl shadow-md border-l-4 border-[#fae240]">
          <div class="flex justify-between items-center">
            <div>
              <p class="text-sm font-medium text-[#035a76]">Pending Reviews</p>
              <p class="text-2xl font-bold text-[#035a76] mt-1">{{ pendingReviews }}</p>
            </div>
            <div class="p-3 rounded-full bg-[#fae240]/20 text-[#fae240]">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path 
                    stroke="currentColor" 
                    stroke-linecap="round" 
                    stroke-linejoin="round" 
                    stroke-width="2" 
                    d="M12 6v6h4m-4 0H8m4 0V6m0 6h4m-4 0H8" 
                  />
                </svg>
            </div>

          </div>
          <p class="text-xs text-[#035a76] mt-2">Appraisals awaiting submission</p>
        </div>
  
        <!-- Overdue Appraisals -->
        <div class="bg-white p-6 rounded-xl shadow-md border-l-4 border-[#e8037e]">
          <div class="flex justify-between items-center">
            <div>
              <p class="text-sm font-medium text-[#035a76]">Overdue Appraisals</p>
              <p class="text-2xl font-bold text-[#035a76] mt-1">{{ overdueAppraisals }}</p>
            </div>
            <div class="p-3 rounded-full bg-[#e8037e]/20 text-[#e8037e]">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path 
                    stroke="currentColor" 
                    stroke-linecap="round" 
                    stroke-linejoin="round" 
                    stroke-width="2" 
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" 
                  />
                </svg>
            </div>

          </div>
          <p class="text-xs text-[#035a76] mt-2">Appraisals past due date</p>
        </div>
  
        <!-- Average Score -->
        <div class="bg-white p-6 rounded-xl shadow-md border-l-4 border-[#76ba51]">
          <div class="flex justify-between items-center">
            <div>
              <p class="text-sm font-medium text-[#035a76]">Average Score</p>
              <p class="text-2xl font-bold text-[#035a76] mt-1">{{ averageScore }}%</p>
            </div>
            <div class="p-3 rounded-full bg-[#76ba51]/20 text-[#76ba51]">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path 
                    stroke="currentColor" 
                    stroke-linecap="round" 
                    stroke-linejoin="round" 
                    stroke-width="2" 
                    d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.783-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" 
                  />
                </svg>
            </div>

          </div>
          <p class="text-xs text-[#035a76] mt-2">Average performance score</p>
        </div>
      </div>
  
      <!-- Header -->
      <div class="flex justify-between items-center mb-6">
        <h1 class="text-2xl font-bold text-[#035a76]">Appraisals Management</h1>
        <div class="flex space-x-4">
          <button @click="openNewAppraisalModal" class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#76ba51] hover:bg-[#76ba51]/90">
            + New Appraisal
          </button>
        </div>
      </div>
  
      <!-- Employee Search -->
      <div class="mb-8 bg-gray-50 p-4 rounded-md">
        <h3 class="text-lg font-medium mb-3">Find Employee</h3>
        <div class="relative">
          <input
            v-model="employeeSearch"
            @input="searchEmployees"
            type="text"
            placeholder="Search by Staff ID or Name"
            class="w-full p-3 border border-gray-300 rounded-md focus:ring-[#035a76] focus:border-[#035a76]"
          >
          <ul v-if="searchResults.length > 0 && employeeSearch" class="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
            <li
              v-for="employee in searchResults"
              :key="employee.id"
              @click="selectEmployee(employee)"
              class="p-3 hover:bg-gray-100 cursor-pointer"
            >
              <div class="flex justify-between">
                <span class="font-medium">{{ employee.name }}</span>
                <span class="text-gray-500 text-sm">{{ employee.position }}</span>
              </div>
              <div class="text-sm text-gray-500">ID: {{ employee.id }}</div>
            </li>
          </ul>
        </div>
  
        <!-- Employee Details -->
        <div v-if="selectedEmployee" class="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4">
          <div class="bg-white p-3 rounded border">
            <p class="text-sm text-gray-500">Employee Name</p>
            <p class="font-medium">{{ selectedEmployee.name }}</p>
          </div>
          <div class="bg-white p-3 rounded border">
            <p class="text-sm text-gray-500">Position</p>
            <p class="font-medium">{{ selectedEmployee.position }}</p>
          </div>
          <div class="bg-white p-3 rounded border">
            <p class="text-sm text-gray-500">Department</p>
            <p class="font-medium">{{ selectedEmployee.department }}</p>
          </div>
          <div class="bg-white p-3 rounded border">
            <p class="text-sm text-gray-500">Staff ID</p>
            <p class="font-medium">{{ selectedEmployee.id }}</p>
          </div>
        </div>
      </div>
  
      <!-- Appraisal Summary -->
      <div v-if="selectedEmployee" class="mb-8">
        <h3 class="text-lg font-medium mb-3">Appraisal Summary</h3>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div class="bg-white p-4 rounded-lg border shadow-sm">
            <p class="text-sm font-medium text-gray-500">Last Appraisal Score</p>
            <p class="text-2xl font-bold">{{ appraisalSummary.lastScore }}%</p>
            <p class="mt-2 text-xs text-gray-500">Most recent performance score</p>
          </div>
          <div class="bg-white p-4 rounded-lg border shadow-sm">
            <p class="text-sm font-medium text-gray-500">Average Score</p>
            <p class="text-2xl font-bold">{{ appraisalSummary.averageScore }}%</p>
            <p class="mt-2 text-xs text-gray-500">Average across all appraisals</p>
          </div>
          <div class="bg-white p-4 rounded-lg border shadow-sm">
            <p class="text-sm font-medium text-gray-500">Total Appraisals</p>
            <p class="text-2xl font-bold">{{ appraisalSummary.totalAppraisals }}</p>
            <p class="mt-2 text-xs text-gray-500">Number of appraisals completed</p>
          </div>
        </div>
      </div>
  
      <!-- Appraisal History -->
      <div v-if="selectedEmployee" class="mb-8">
        <div class="flex justify-between items-center mb-4">
          <h3 class="text-lg font-medium">Appraisal History</h3>
          <div class="flex space-x-2">
            <select v-model="statusFilter" class="text-sm border rounded px-3 py-1 focus:ring-[#035a76] focus:border-[#035a76]">
              <option value="all">All Statuses</option>
              <option value="completed">Completed</option>
              <option value="pending">Pending</option>
              <option value="overdue">Overdue</option>
            </select>
            <select v-model="periodFilter" class="text-sm border rounded px-3 py-1 focus:ring-[#035a76] focus:border-[#035a76]">
              <option value="all">All Periods</option>
              <option value="2025-Q1">2025 Q1</option>
              <option value="2024-Q4">2024 Q4</option>
              <option value="2024-Q3">2024 Q3</option>
            </select>
          </div>
        </div>
  
        <div class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Period</th>
                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Score</th>
                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reviewer</th>
                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr v-for="appraisal in filteredHistory" :key="appraisal.id">
                <td class="px-6 py-4 whitespace-nowrap">{{ appraisal.period }}</td>
                <td class="px-6 py-4 whitespace-nowrap">{{ appraisal.score }}%</td>
                <td class="px-6 py-4 whitespace-nowrap">{{ formatDate(appraisal.date) }}</td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <span class="px-2 py-1 text-xs rounded-full" :class="{
                    'bg-green-100 text-green-800': appraisal.status === 'completed',
                    'bg-yellow-100 text-yellow-800': appraisal.status === 'pending',
                    'bg-red-100 text-red-800': appraisal.status === 'overdue'
                  }">
                    {{ appraisal.status }}
                  </span>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ appraisal.reviewer || 'N/A' }}</td>
                <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <button @click="viewAppraisalDetails(appraisal)" class="text-[#035a76] hover:text-[#035a76]/70 mr-2">
                    View
                  </button>
                  <button v-if="appraisal.status === 'pending'" @click="cancelAppraisal(appraisal.id)" class="text-red-600 hover:text-red-900">
                    Cancel
                  </button>
                </td>
              </tr>
              <tr v-if="filteredHistory.length === 0">
                <td colspan="6" class="px-6 py-4 text-center text-sm text-gray-500">
                  No appraisals found
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
  
      <!-- New Appraisal Modal -->
      <div v-if="showNewAppraisalModal" class="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div class="bg-white rounded-lg shadow-xl w-full max-w-2xl">
          <div class="px-6 py-4 border-b">
            <h3 class="text-lg font-medium text-gray-900">New Appraisal</h3>
          </div>
          <div class="px-6 py-4">
            <div class="space-y-4">
              <div>
                <label class="block text-sm font-medium text-gray-700">Appraisal Period</label>
                <select v-model="newAppraisal.period" class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#035a76] focus:border-[#035a76]">
                  <option value="2025-Q1">2025 Q1</option>
                  <option value="2025-Q2">2025 Q2</option>
                  <option value="2025-Q3">2025 Q3</option>
                  <option value="2025-Q4">2025 Q4</option>
                </select>
              </div>
  
              <div>
                <label class="block text-sm font-medium text-gray-700">Performance Score (%)</label>
                <input v-model.number="newAppraisal.score" type="number" min="0" max="100" required
                       class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#035a76] focus:border-[#035a76]"
                       placeholder="Enter score (0-100)">
              </div>
  
              <div>
                <label class="block text-sm font-medium text-gray-700">Comments</label>
                <textarea v-model="newAppraisal.comments" rows="4" required
                          class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#035a76] focus:border-[#035a76]"
                          placeholder="Enter appraisal comments..."></textarea>
              </div>
  
              <div>
                <label class="block text-sm font-medium text-gray-700">Goals for Next Period</label>
                <textarea v-model="newAppraisal.goals" rows="3"
                          class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#035a76] focus:border-[#035a76]"
                          placeholder="Enter goals for next period..."></textarea>
              </div>
  
              <div>
                <label class="block text-sm font-medium text-gray-700">Attachment (Optional)</label>
                <input type="file" @change="handleFileUpload"
                       class="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-[#035a76]/10 file:text-[#035a76] hover:file:bg-[#035a76]/20">
              </div>
            </div>
          </div>
          <div class="px-6 py-4 border-t flex justify-end space-x-3">
            <button @click="showNewAppraisalModal = false" type="button" class="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#035a76]">
              Cancel
            </button>
            <button @click="submitAppraisal" type="button" class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#76ba51] hover:bg-[#76ba51]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#035a76]">
              Submit Appraisal
            </button>
          </div>
        </div>
      </div>
  
      <!-- Appraisal Details Modal -->
      <div v-if="selectedAppraisal" class="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div class="bg-white rounded-lg shadow-xl w-full max-w-2xl">
          <div class="px-6 py-4 border-b flex justify-between items-center">
            <h3 class="text-lg font-medium text-gray-900">Appraisal Details - {{ selectedAppraisal.period }}</h3>
            <button @click="selectedAppraisal = null" class="text-gray-400 hover:text-gray-500">
              <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div class="px-6 py-4">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div class="space-y-4">
                <div>
                  <p class="text-sm text-gray-500">Employee</p>
                  <p class="font-medium">{{ selectedAppraisal.employeeName }}</p>
                </div>
                <div>
                  <p class="text-sm text-gray-500">Period</p>
                  <p class="font-medium">{{ selectedAppraisal.period }}</p>
                </div>
                <div>
                  <p class="text-sm text-gray-500">Score</p>
                  <p class="font-medium">{{ selectedAppraisal.score }}%</p>
                </div>
              </div>
              <div class="space-y-4">
                <div>
                  <p class="text-sm text-gray-500">Status</p>
                  <span class="px-2 py-1 text-xs rounded-full" :class="{
                    'bg-green-100 text-green-800': selectedAppraisal.status === 'completed',
                    'bg-yellow-100 text-yellow-800': selectedAppraisal.status === 'pending',
                    'bg-red-100 text-red-800': selectedAppraisal.status === 'overdue'
                  }">
                    {{ selectedAppraisal.status }}
                  </span>
                </div>
                <div>
                  <p class="text-sm text-gray-500">Date</p>
                  <p class="font-medium">{{ formatDateTime(selectedAppraisal.date) }}</p>
                </div>
                <div v-if="selectedAppraisal.reviewer">
                  <p class="text-sm text-gray-500">Reviewer</p>
                  <p class="font-medium">{{ selectedAppraisal.reviewer }}</p>
                </div>
              </div>
            </div>
  
            <div class="mt-6">
              <p class="text-sm text-gray-500">Comments</p>
              <p class="mt-1 p-3 bg-gray-50 rounded">{{ selectedAppraisal.comments }}</p>
            </div>
  
            <div v-if="selectedAppraisal.goals" class="mt-4">
              <p class="text-sm text-gray-500">Goals for Next Period</p>
              <p class="mt-1 p-3 bg-gray-50 rounded">{{ selectedAppraisal.goals }}</p>
            </div>
  
            <div v-if="selectedAppraisal.attachment" class="mt-4">
              <p class="text-sm text-gray-500">Attachment</p>
              <a href="#" @click.prevent="downloadAttachment" class="mt-1 inline-flex items-center text-[#035a76] hover:underline">
                <svg class="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                </svg>
                {{ selectedAppraisal.attachment.name }}
              </a>
            </div>
          </div>
          <div v-if="isManager && selectedAppraisal.status === 'pending'" class="px-6 py-4 border-t flex justify-end space-x-3">
            <button @click="submitAppraisalReview(selectedAppraisal.id)" class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500">
              Submit
            </button>
            <button @click="reviseAppraisal(selectedAppraisal.id)" class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-yellow-600 hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500">
              Revise
            </button>
          </div>
        </div>
      </div>
    </div>
    </main>
    </div>
  </template>
  
  <script setup>
  import { ref, computed } from 'vue';
  import HrSidebar from '../../components/hr/HrSidebar.vue';
  import HrTopbar from '../../components/hr/HrTopbar.vue';
  
  const isSidebarOpen = ref(true);
  const isDarkMode = ref(false);
  const toggleSidebar = () => {
    isSidebarOpen.value = !isSidebarOpen.value;
  };

  const currentPageTitle = ref('Appraisals');
  
  // Sample data
  const sampleEmployees = [
    { id: 'EMP001', name: 'Kwame Mensah', position: 'Mathematics Teacher', department: 'Academics' },
    { id: 'EMP002', name: 'Ama Asante', position: 'Science Teacher', department: 'Academics' },
    { id: 'EMP003', name: 'Yaw Boateng', position: 'HR Manager', department: 'Administration' },
    { id: 'EMP004', name: 'Esi Coleman', position: 'Finance Officer', department: 'Administration' },
  ];
  
  const sampleAppraisalSummary = {
    lastScore: 85,
    averageScore: 88,
    totalAppraisals: 3,
  };
  
  const sampleAppraisalHistory = [
    {
      id: 1,
      employeeName: 'Kwame Mensah',
      period: '2024-Q4',
      score: 90,
      date: '2024-12-15',
      status: 'completed',
      comments: 'Excellent performance in classroom management and curriculum delivery.',
      goals: 'Develop advanced teaching materials for next semester.',
      reviewer: 'Yaw Boateng',
      attachment: { name: 'performance_review.pdf' },
    },
    {
      id: 2,
      employeeName: 'Kwame Mensah',
      period: '2024-Q3',
      score: 85,
      date: '2024-09-30',
      status: 'completed',
      comments: 'Strong contribution to department goals, needs improvement in punctuality.',
      goals: 'Attend time management workshop.',
      reviewer: 'Esi Coleman',
      attachment: null,
    },
    {
      id: 3,
      employeeName: 'Kwame Mensah',
      period: '2025-Q1',
      score: 0,
      date: '2025-04-01',
      status: 'pending',
      comments: '',
      goals: '',
      reviewer: null,
      attachment: null,
    },
  ];
  
  // Reactive state
  const employeeSearch = ref('');
  const searchResults = ref([]);
  const selectedEmployee = ref(null);
  const appraisalSummary = ref({});
  const statusFilter = ref('all');
  const periodFilter = ref('all');
  const showNewAppraisalModal = ref(false);
  const selectedAppraisal = ref(null);
  const isManager = ref(true);
  
  const newAppraisal = ref({
    period: '2025-Q1',
    score: 0,
    comments: '',
    goals: '',
    attachment: null,
  });
  
  // Summary data
  const totalAppraisals = ref(10);
  const pendingReviews = ref(2);
  const overdueAppraisals = ref(1);
  const averageScore = ref(87);
  
  // Computed properties
  const filteredHistory = computed(() => {
    let filtered = sampleAppraisalHistory.filter(appraisal => appraisal.employeeName === selectedEmployee.value?.name);
  
    if (statusFilter.value !== 'all') {
      filtered = filtered.filter(appraisal => appraisal.status === statusFilter.value);
    }
  
    if (periodFilter.value !== 'all') {
      filtered = filtered.filter(appraisal => appraisal.period === periodFilter.value);
    }
  
    return filtered;
  });
  
  // Methods
  const searchEmployees = () => {
    if (employeeSearch.value.length < 2) {
      searchResults.value = [];
      return;
    }
  
    const searchTerm = employeeSearch.value.toLowerCase();
    searchResults.value = sampleEmployees.filter(emp =>
      emp.name.toLowerCase().includes(searchTerm) ||
      emp.id.toLowerCase().includes(searchTerm)
    );
  };
  
  const selectEmployee = (employee) => {
    selectedEmployee.value = employee;
    employeeSearch.value = employee.name;
    searchResults.value = [];
    appraisalSummary.value = sampleAppraisalSummary;
  };
  
  const openNewAppraisalModal = () => {
    if (!selectedEmployee.value) {
      alert('Please select an employee first');
      return;
    }
    newAppraisal.value = { period: '2025-Q1', score: 0, comments: '', goals: '', attachment: null };
    showNewAppraisalModal.value = true;
  };
  
  const submitAppraisal = () => {
    if (newAppraisal.value.score < 0 || newAppraisal.value.score > 100) {
      alert('Score must be between 0 and 100');
      return;
    }
    if (!newAppraisal.value.comments) {
      alert('Comments are required');
      return;
    }
    alert(`Appraisal submitted for ${selectedEmployee.value.name}`);
    showNewAppraisalModal.value = false;
  };
  
  const viewAppraisalDetails = (appraisal) => {
    selectedAppraisal.value = appraisal;
  };
  
  const submitAppraisalReview = (id) => {
    const appraisal = sampleAppraisalHistory.find(a => a.id === id);
    if (appraisal) {
      appraisal.status = 'completed';
      appraisal.reviewer = 'Current User';
    }
    selectedAppraisal.value = null;
  };
  
  const reviseAppraisal = (id) => {
    const appraisal = sampleAppraisalHistory.find(a => a.id === id);
    if (appraisal) {
      appraisal.status = 'pending';
      appraisal.reviewer = null;
    }
    selectedAppraisal.value = null;
  };
  
  const cancelAppraisal = (id) => {
    const index = sampleAppraisalHistory.findIndex(a => a.id === id);
    if (index !== -1) {
      sampleAppraisalHistory.splice(index, 1);
    }
  };
  
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      newAppraisal.value.attachment = {
        name: file.name,
        file: file,
      };
    }
  };
  
  const downloadAttachment = () => {
    alert(`Downloading ${selectedAppraisal.value.attachment.name}`);
  };
  
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };
  
  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };
  </script>
  
  <style scoped>
  /* Additional custom styles can go here if needed */
  </style>